import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useApplicationConfig } from '@bonusly/application';
import { Box } from '@bonusly/design-system';

import { buildURL } from '../../lib/url/util/url';
import { dispatchBizyOpenEvent } from '../bizy-side-panel/hooks/use-bizy-open-listener';

function toRailsPath(pathname: string): string {
	const shellPrefix = pathname.startsWith('/super_admin/') ? '/super_admin' : '/admin';
	const afterShell = pathname.slice(shellPrefix.length);

	if (afterShell.startsWith('/r/') || afterShell === '/r') {
		return afterShell.slice(2) || '/';
	}

	return pathname;
}

/**
 * Reads the current seasonal color mode from Kaleidoscope's localStorage so it
 * can be passed to the Rails iframe as a URL param. Must run in Kaleidoscope's
 * rendering context — the Rails iframe is a different origin and cannot access
 * this localStorage directly.
 *
 * Returns 'light' | 'dark' when a seasonal theme is active, null otherwise.
 */
function getSeasonalColorMode(): 'light' | 'dark' | null {
	try {
		// active-mode is written synchronously in toggleTheme (new code path).
		const activeMode = localStorage.getItem('bonusly:seasonal-theme:active-mode');
		const strategy = localStorage.getItem('bonusly:seasonal-theme:color-mode-strategy');

		// Fallback: scan for a preference key set by older toggleTheme code.
		// Preference keys match bonusly:seasonal-theme:<name> with no further colons.
		const EXCLUDED = new Set(['bonusly:seasonal-theme:active-mode', 'bonusly:seasonal-theme:color-mode-strategy', 'bonusly:seasonal-theme:saved-color-mode']);
		let isActive = activeMode === 'dark' || activeMode === 'light';
		if (!isActive) {
			for (let i = 0; i < localStorage.length; i++) {
				const k = localStorage.key(i);
				if (k && /^bonusly:seasonal-theme:[^:]+$/.test(k) && !EXCLUDED.has(k) && localStorage.getItem(k) === 'true') {
					isActive = true;
					break;
				}
			}
		}

		if (!isActive) return null;

		if (strategy === 'force-dark') return 'dark';
		// time-based (spring) or unknown strategy: derive from current hour
		const hour = new Date().getHours();
		return hour < 6 || hour >= 19 ? 'dark' : 'light';
	} catch {
		return null;
	}
}

/**
 * Renders a server-rendered Rails page inside an iframe, keeping the
 * Kaleidoscope shell (admin nav, Bizy side panel) mounted around it.
 *
 * Links inside the iframe use <base target="_top"> which navigates the top
 * window. Rails detects these top-level navigations (via Sec-Fetch-Dest)
 * and redirects back to the equivalent Kaleidoscope route, preserving the
 * admin shell. The iframe key includes search params so it remounts cleanly
 * when query params change (e.g., pagination).
 *
 * seasonal_color_mode is appended when a seasonal theme is active so the
 * Rails server can render the correct initial color mode. The iframe is a
 * different origin and cannot read Kaleidoscope's localStorage directly.
 */
export const AdminLegacyPage: FC = () => {
	const recognizeOrigin = useApplicationConfig('recognizeOrigin');
	const location = useLocation();
	// Computed once on mount — stable for the lifetime of this route visit.
	// Recomputed on full remount (navigation away and back, or full page reload).
	const [seasonalMode] = useState(() => getSeasonalColorMode());

	// `MessageEvent.origin` is always a bare origin (scheme://host[:port]) with no
	// trailing slash, but `recognizeOrigin` config can include one (prod is
	// "https://bonus.ly/"). Canonicalize so the strict comparison below doesn't
	// drop every legitimate message. Falls back to a trailing-slash trim for
	// relative test configs (e.g. "/recognize") that `URL` can't parse.
	const trustedOrigin = useMemo(() => {
		try {
			return new URL(recognizeOrigin).origin;
		} catch {
			return recognizeOrigin.replace(/\/+$/, '');
		}
	}, [recognizeOrigin]);

	// Rails pages embedded here are a different origin and can't dispatch the
	// `bizy:open` window event the parent shell listens for. Instead they
	// `postMessage` a `bizy:open` request (e.g. the User Accounts prompt bar);
	// we validate the origin and forward it to the shell's Bizy side panel.
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== trustedOrigin) return;

			const data = event.data as { type?: string; detail?: Partial<Record<'source' | 'pageContext' | 'starterMessage', string>> } | null;
			if (!data || data.type !== 'bizy:open' || !data.detail) return;

			const { source, pageContext, starterMessage } = data.detail;
			dispatchBizyOpenEvent({
				source: typeof source === 'string' ? source : 'admin_legacy_page',
				pagePath: window.location.pathname,
				pageContext: typeof pageContext === 'string' ? pageContext : '',
				starterMessage: typeof starterMessage === 'string' ? starterMessage : undefined,
			});
		};

		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [trustedOrigin]);

	const railsPath = toRailsPath(location.pathname);
	const iframeSrc = useMemo(() => {
		const base = buildURL(recognizeOrigin)(railsPath);
		const separator = location.search ? `${location.search}&_iframe=1` : '?_iframe=1';
		const seasonalParam = seasonalMode ? `&seasonal_color_mode=${seasonalMode}` : '';
		return `${base}${separator}${seasonalParam}`;
	}, [recognizeOrigin, railsPath, location.search, seasonalMode]);

	return (
		<Box w="100%" h="100%">
			<iframe
				key={`${location.pathname}${location.search}`}
				src={iframeSrc}
				title="Admin"
				style={{
					width: '100%',
					height: '100%',
					border: 'none',
					display: 'block',
				}}
			/>
		</Box>
	);
};
